/**
 * Admin › Finanças › Campanhas de cupom (W10-TARGETED-COUPONS, D493, ADR-0109; doc 17 section 3.3 L5) - the pure view-model behind the screen where an operator proposes a
 * segmented coupon campaign, a DIFFERENT operator approves it (the approval RESERVES the budget in the incentive fund) and either may end it, over `@vello/api-client`
 * `CouponCampaignsAdminApi`.
 *
 *  - who may do what (`coupon_campaign:view`, `:manage` = the maker, the kill and the restart, `:approve` = the checker; all GEO - bound to the market in the header scope);
 *  - whether THIS operator may decide a campaign (never the one who proposed it - the server refuses `CAMPAIGN_SELF_APPROVAL` anyway, the screen hides the button and says
 *    why), whether it may be ended (an active one) and restarted (an ended one, as a NEW proposal);
 *  - the draft checks of a proposal against the BOUNDS the governed campaign policy publishes (the server enforces them; the screen never offers a value past them).
 * No segment is typed (a closed list read through the vocabulary); nothing here names a country, a currency or an amount.
 */
import type { CouponCampaignView, CouponCampaignsListView } from '@vello/api-client';
import { grantsAllow, type KnownGrants } from './console-access.js';

export type CampaignTone = 'neutral' | 'success' | 'warning' | 'danger';

export function couponCampaignAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayManage: boolean; readonly mayApprove: boolean } {
  if (grants === undefined) return { mayView: true, mayManage: true, mayApprove: true };
  return {
    mayView: grantsAllow(grants, { permission: 'coupon_campaign:view', target: 'GEO' }),
    mayManage: grantsAllow(grants, { permission: 'coupon_campaign:manage', target: 'GEO' }),
    mayApprove: grantsAllow(grants, { permission: 'coupon_campaign:approve', target: 'GEO' }),
  };
}

export function campaignStateTone(state: string): CampaignTone {
  return state === 'RUNNING' ? 'success' : state === 'PROPOSED' || state === 'SCHEDULED' || state === 'WINDING_DOWN' ? 'warning' : state === 'REFUSED' ? 'danger' : 'neutral';
}

export function campaignPolicyTone(state: CouponCampaignsListView['fund']['state']): CampaignTone {
  return state === 'ACTIVE' ? 'success' : state === 'INVALID' ? 'danger' : state === 'DISABLED' ? 'warning' : 'neutral';
}

/** Why this operator may not approve a campaign (or undefined when he may). */
export function campaignApproveBlock(c: Pick<CouponCampaignView, 'status' | 'createdBy'>, operatorId: string | undefined, mayApprove: boolean, selfApproval = false): 'NOT_PENDING' | 'NO_PERMISSION' | 'OWN_PROPOSAL' | undefined {
  if (c.status !== 'PROPOSED') return 'NOT_PENDING';
  if (!mayApprove) return 'NO_PERMISSION';
  if (!selfApproval && operatorId !== undefined && c.createdBy?.id === operatorId) return 'OWN_PROPOSAL';
  return undefined;
}

/** Only an active campaign (scheduled, issuing or winding down) can be ended early; an ended one can be restarted as a new proposal. */
export const campaignCanEnd = (c: Pick<CouponCampaignView, 'status'>, mayManage: boolean): boolean => mayManage && c.status === 'ACTIVE';
export const campaignCanRestart = (c: Pick<CouponCampaignView, 'status'>, mayManage: boolean): boolean => mayManage && c.status === 'ENDED';

/** The issuance windows (days) a campaign may run: a closed list, cut at the policy's longest window. */
export const CAMPAIGN_WINDOW_DAYS = [1, 3, 7, 14, 21, 30] as const;
export const windowDayChoices = (maxWindowDays: number): readonly number[] => CAMPAIGN_WINDOW_DAYS.filter((d) => d <= maxWindowDays);
/** The coupon validities (days) offered: a closed list, cut at the policy's longest validity. */
export const CAMPAIGN_VALIDITY_DAYS = [1, 3, 7, 14, 21, 30] as const;
export const validityDayChoices = (maxValidityDays: number): readonly number[] => CAMPAIGN_VALIDITY_DAYS.filter((d) => d <= maxValidityDays);
export const INCIDENT_LOOKBACK_DAYS = [1, 3, 7, 14, 30] as const;
export const PER_PERSON_COUNTS = [1, 2, 3] as const;

/**
 * D609 (ADR-0119): the closed choices of the quote and rating triggers, the validity in hours and the frequency rules. The server re-checks every range; the screen never offers a value
 * the kernel would refuse (a quote's lookback must outlast its wait, so the lists are cut against the wait chosen).
 */
export const TRIGGER_WAIT_MINUTES = [15, 30, 60, 120, 360, 720, 1440] as const;
export const TRIGGER_LOOKBACK_HOURS = [6, 12, 24, 48, 72, 168] as const;
export const RATING_MAX_STARS = [1, 2, 3, 4] as const;
export const VALIDITY_HOUR_CHOICES = [1, 2, 3, 6, 12, 24, 48, 72] as const;
export const MIN_INTERVAL_HOURS = [24, 72, 168, 336, 720] as const;
export const ROLLING_WINDOW_DAYS = [7, 14, 30, 60, 90] as const;
export const ROLLING_MAX_COUNTS = [1, 2, 3] as const;
/** The segments that wait for a trigger EVENT (a quote, a rating) rather than judge the rider's standing. */
export const isTriggerSegment = (segment: string): boolean => segment === 'QUOTE_NO_REQUEST' || segment === 'AFTER_LOW_RATING';
export const validityHourChoices = (maxValidityDays: number): readonly number[] => VALIDITY_HOUR_CHOICES.filter((h) => h <= maxValidityDays * 24);
/** The lookbacks (hours) that outlast the chosen wait (minutes). */
export const lookbackHourChoices = (waitMinutes: number | undefined): readonly number[] => TRIGGER_LOOKBACK_HOURS.filter((h) => waitMinutes === undefined || h * 60 > waitMinutes);

/** The channel sets a campaign may use: the in-app inbox always, plus ONE other channel the market's broadcast policy enables. */
export function channelChoices(enabled: readonly string[]): readonly (readonly string[])[] {
  return [['IN_APP'], ...enabled.filter((c) => c !== 'IN_APP').map((c) => ['IN_APP', c])];
}

export type CampaignDraftIssue =
  | 'SEGMENT_REQUIRED' | 'ENVELOPE_REQUIRED' | 'KIND_REQUIRED' | 'PERCENT_REQUIRED' | 'PERCENT_OVER_POLICY' | 'VALUE_REQUIRED' | 'VALUE_OVER_POLICY' | 'BUDGET_REQUIRED' | 'BUDGET_OVER_POLICY'
  | 'BUDGET_BELOW_VALUE' | 'VALIDITY_REQUIRED' | 'WINDOW_REQUIRED' | 'LOOKBACK_REQUIRED' | 'PERIOD_REQUIRED' | 'REASON_REQUIRED'
  | 'WAIT_REQUIRED' | 'TRIGGER_LOOKBACK_REQUIRED' | 'RATING_REQUIRED' | 'ROLLING_INCOMPLETE';

export interface CampaignDraft {
  readonly segment: string;
  readonly envelope: string;
  readonly discountKind: string;
  readonly percentBp: number | undefined;
  readonly valueMinor: bigint | undefined;
  readonly budgetCapMinor: bigint | undefined;
  readonly validityDays: number | undefined;
  /** D609: the validity in hours instead of days (exactly one of the two is set). */
  readonly validityHours: number | undefined;
  readonly windowDays: number | undefined;
  readonly lookbackDays: number | undefined;
  readonly perPersonCount: number;
  readonly perPersonPeriod: string;
  readonly channels: readonly string[];
  readonly reasonCode: string;
  /** D609: the wait before the offer (minutes), how far back the trigger event may be (hours) and the star threshold - QUOTE_NO_REQUEST and AFTER_LOW_RATING only. */
  readonly waitMinutes: number | undefined;
  readonly triggerLookbackHours: number | undefined;
  readonly ratingMaxStars: number | undefined;
  /** D609: the frequency rules (all optional). */
  readonly minIntervalHours: number | undefined;
  readonly skipIfActiveCoupon: boolean;
  readonly rollingWindowDays: number | undefined;
  readonly rollingMaxCount: number | undefined;
}

export const EMPTY_CAMPAIGN_DRAFT: CampaignDraft = {
  segment: '', envelope: '', discountKind: 'PERCENT', percentBp: undefined, valueMinor: undefined, budgetCapMinor: undefined, validityDays: undefined, validityHours: undefined, windowDays: undefined, lookbackDays: undefined,
  perPersonCount: 1, perPersonPeriod: 'CAMPAIGN', channels: ['IN_APP'], reasonCode: '',
  waitMinutes: undefined, triggerLookbackHours: undefined, ratingMaxStars: undefined, minIntervalHours: undefined, skipIfActiveCoupon: false, rollingWindowDays: undefined, rollingMaxCount: undefined,
};

const bigOf = (m: { readonly amountMinor: string } | null): bigint | undefined => (m !== null && /^\d+$/.test(m.amountMinor) ? BigInt(m.amountMinor) : undefined);

/** The first thing wrong with a proposal draft against the policy's published bounds, or undefined. The server re-checks every one of them. */
export function campaignDraftIssue(d: CampaignDraft, policy: CouponCampaignsListView['campaignPolicy'], reasons: readonly string[]): CampaignDraftIssue | undefined {
  if (!policy.segments.includes(d.segment)) return 'SEGMENT_REQUIRED';
  if (!policy.envelopes.includes(d.envelope)) return 'ENVELOPE_REQUIRED';
  if (d.discountKind !== 'PERCENT' && d.discountKind !== 'FIXED') return 'KIND_REQUIRED';
  if (d.discountKind === 'PERCENT') {
    if (d.percentBp === undefined || !Number.isInteger(d.percentBp) || d.percentBp < 1) return 'PERCENT_REQUIRED';
    if (d.percentBp > policy.maxPercentBp) return 'PERCENT_OVER_POLICY';
  }
  if (d.valueMinor === undefined || d.valueMinor <= 0n) return 'VALUE_REQUIRED';
  const maxValue = bigOf(policy.maxCouponValue);
  if (maxValue !== undefined && d.valueMinor > maxValue) return 'VALUE_OVER_POLICY';
  if (d.budgetCapMinor === undefined || d.budgetCapMinor <= 0n) return 'BUDGET_REQUIRED';
  const maxBudget = bigOf(policy.maxBudget);
  if (maxBudget !== undefined && d.budgetCapMinor > maxBudget) return 'BUDGET_OVER_POLICY';
  if (d.budgetCapMinor < d.valueMinor) return 'BUDGET_BELOW_VALUE';
  // exactly one of days / hours, each from its closed list
  const dayOk = d.validityDays !== undefined && validityDayChoices(policy.maxValidityDays).includes(d.validityDays);
  const hourOk = d.validityHours !== undefined && validityHourChoices(policy.maxValidityDays).includes(d.validityHours);
  if ((d.validityDays === undefined) === (d.validityHours === undefined) || !(dayOk || hourOk)) return 'VALIDITY_REQUIRED';
  if (d.windowDays === undefined || !windowDayChoices(policy.maxWindowDays).includes(d.windowDays)) return 'WINDOW_REQUIRED';
  if (d.segment === 'AFTER_INCIDENT' && (d.lookbackDays === undefined || !(INCIDENT_LOOKBACK_DAYS as readonly number[]).includes(d.lookbackDays))) return 'LOOKBACK_REQUIRED';
  if (isTriggerSegment(d.segment)) {
    if (d.segment === 'QUOTE_NO_REQUEST' && (d.waitMinutes === undefined || !(TRIGGER_WAIT_MINUTES as readonly number[]).includes(d.waitMinutes))) return 'WAIT_REQUIRED';
    if (d.waitMinutes !== undefined && !(TRIGGER_WAIT_MINUTES as readonly number[]).includes(d.waitMinutes)) return 'WAIT_REQUIRED';
    if (d.triggerLookbackHours === undefined || !lookbackHourChoices(d.waitMinutes).includes(d.triggerLookbackHours)) return 'TRIGGER_LOOKBACK_REQUIRED';
    if (d.segment === 'AFTER_LOW_RATING' && (d.ratingMaxStars === undefined || !(RATING_MAX_STARS as readonly number[]).includes(d.ratingMaxStars))) return 'RATING_REQUIRED';
  }
  if ((d.rollingWindowDays === undefined) !== (d.rollingMaxCount === undefined)) return 'ROLLING_INCOMPLETE';
  if (!(PER_PERSON_COUNTS as readonly number[]).includes(d.perPersonCount) || !['WEEK', 'MONTH', 'CAMPAIGN'].includes(d.perPersonPeriod)) return 'PERIOD_REQUIRED';
  if (!reasons.includes(d.reasonCode)) return 'REASON_REQUIRED';
  return undefined;
}

/** How many coupons the budget buys (the server reserves exactly this many holds): floor(budget / value), never more than the cap. */
export function couponCountOf(d: Pick<CampaignDraft, 'valueMinor' | 'budgetCapMinor'>): number | undefined {
  if (d.valueMinor === undefined || d.budgetCapMinor === undefined || d.valueMinor <= 0n || d.budgetCapMinor < d.valueMinor) return undefined;
  const n = d.budgetCapMinor / d.valueMinor;
  return n > 100_000n ? 100_000 : Number(n);
}

/** The body of a proposal: the window starts now and runs `windowDays`; an actor, a currency or a protected attribute is never part of it. */
export function campaignProposalBody(marketId: string, d: CampaignDraft, nowIso: string): Record<string, unknown> {
  return {
    marketId,
    segment: d.segment,
    envelope: d.envelope,
    discountKind: d.discountKind,
    ...(d.discountKind === 'PERCENT' ? { percentBp: d.percentBp ?? 0 } : {}),
    valueMinor: (d.valueMinor ?? 0n).toString(),
    ...(d.validityHours !== undefined ? { validityHours: d.validityHours } : { validityDays: d.validityDays ?? 0 }),
    budgetCapMinor: (d.budgetCapMinor ?? 0n).toString(),
    perPersonCount: d.perPersonCount,
    perPersonPeriod: d.perPersonPeriod,
    windowFrom: nowIso,
    windowTo: new Date(Date.parse(nowIso) + (d.windowDays ?? 0) * 86_400_000).toISOString(),
    channels: [...d.channels],
    ...(d.segment === 'AFTER_INCIDENT' ? { incidentLookbackDays: d.lookbackDays ?? 0 } : {}),
    // D609: the trigger terms belong to their segments; the frequency rules are optional and sent only when chosen
    ...(isTriggerSegment(d.segment) ? { triggerLookbackHours: d.triggerLookbackHours ?? 0 } : {}),
    ...(isTriggerSegment(d.segment) && d.waitMinutes !== undefined ? { waitMinutes: d.waitMinutes } : {}),
    ...(d.segment === 'AFTER_LOW_RATING' ? { ratingMaxStars: d.ratingMaxStars ?? 0 } : {}),
    ...(d.minIntervalHours !== undefined ? { minIntervalHours: d.minIntervalHours } : {}),
    ...(d.skipIfActiveCoupon ? { skipIfActiveCoupon: true } : {}),
    ...(d.rollingWindowDays !== undefined && d.rollingMaxCount !== undefined ? { rollingWindowDays: d.rollingWindowDays, rollingMaxCount: d.rollingMaxCount } : {}),
    reasonCode: d.reasonCode,
  };
}

/** How much of the reservation is spoken for, in basis points (issued coupons over the coupons reserved; capped at 10 000). */
export function campaignIssuedBp(c: Pick<CouponCampaignView, 'issued' | 'couponCount'>): number {
  return c.couponCount <= 0 ? 0 : Math.min(10_000, Math.floor((c.issued * 10_000) / c.couponCount));
}
